import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PowerOff, ShieldCheck } from 'lucide-react'
import Button from '@/components/button'
import ChipSelect from '@/components/chip-select'
import ConfirmDialog from '@/components/confirm-dialog'
import DataTable from '@/components/data-table'
import type { Column } from '@/components/data-table'
import FilterBar from '@/components/filter-bar'
import FormField from '@/components/form-field'
import LookupSelect from '@/components/lookup-select'
import PageHeader from '@/components/page-header'
import StatusBadge from '@/components/status-badge'
import { useAtualizarSituacaoCredencialMutation } from '@/http/mutations/credenciais'
import { useCredenciaisQuery } from '@/http/queries/credenciais'
import { useClientesLookupQuery, useOperadorasLookupQuery } from '@/http/queries/lookups'
import type { CredencialResumo } from '@/types/credencial'
import type { LookupItem } from '@/types/lookup'
import { STATUS_CREDENCIAL, situacaoCredencialBadges } from '@/types/credencial'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import { formatDateDefault, orDash } from '@/utility/Utils'
import { contarFiltrosDoPainel, filtroVazio, montarChips, paraFiltroApi } from './filtro'
import type { FiltroCredenciais } from './filtro'
import styles from './style.module.css'

const CredenciaisList = () => {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [busca, setBusca] = useState('')
  const [aplicado, setAplicado] = useState<FiltroCredenciais>(filtroVazio)
  const [rascunho, setRascunho] = useState<FiltroCredenciais>(filtroVazio)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [alvo, setAlvo] = useState<CredencialResumo | null>(null)
  const [termoCliente, setTermoCliente] = useState('')
  const [clienteEscolhido, setClienteEscolhido] = useState<LookupItem | null>(null)
  const [termoOperadora, setTermoOperadora] = useState('')
  const [operadoraEscolhida, setOperadoraEscolhida] = useState<LookupItem | null>(null)

  const buscaDebounced = useDebouncedValue(busca)
  const termoClienteDebounced = useDebouncedValue(termoCliente)
  const termoOperadoraDebounced = useDebouncedValue(termoOperadora)

  const clientes = useClientesLookupQuery(
    termoClienteDebounced,
    termoClienteDebounced.trim() !== '',
  )
  const operadoras = useOperadorasLookupQuery(
    termoOperadoraDebounced,
    termoOperadoraDebounced.trim() !== '',
  )

  useEffect(() => {
    setAplicado((atual) =>
      atual.busca === buscaDebounced ? atual : { ...atual, busca: buscaDebounced },
    )
    setPageNumber(1)
  }, [buscaDebounced])

  const filtro = useMemo(
    () => ({ ...paraFiltroApi(aplicado, pageSize), pageNumber }),
    [aplicado, pageNumber, pageSize],
  )

  const { data: page, error, isFetching, refetch } = useCredenciaisQuery(filtro)
  const atualizarSituacao = useAtualizarSituacaoCredencialMutation()

  const efetivar = (proximo: FiltroCredenciais) => {
    setAplicado(proximo)
    setRascunho(proximo)
    setBusca(proximo.busca)
    setPageNumber(1)
  }

  const alterarRascunho = <C extends keyof FiltroCredenciais>(
    campo: C,
    valor: FiltroCredenciais[C],
  ) => {
    setRascunho((atual) => ({ ...atual, [campo]: valor }))
  }

  const alternarSituacao = (valores: string[]) => {
    const nova = valores.at(-1) as FiltroCredenciais['situacao'] | undefined
    alterarRascunho('situacao', nova ?? STATUS_CREDENCIAL.Todas)
  }

  const columns: Column<CredencialResumo>[] = [
    {
      key: 'nome',
      header: 'Credencial',
      render: (credencial) => (
        <div className={styles.stack}>
          <strong>{orDash(credencial.nome)}</strong>
          <span className={styles.muted}>Prestador {orDash(credencial.codigoPrestador)}</span>
        </div>
      ),
    },
    { key: 'cliente', header: 'Cliente', render: (c) => orDash(c.nomeCliente) },
    { key: 'operadora', header: 'Operadora', render: (c) => orDash(c.operadora) },
    { key: 'tabela', header: 'Tabela', render: (c) => orDash(c.tabela) },
    { key: 'criacao', header: 'Criada em', render: (c) => formatDateDefault(c.createdAt) },
    {
      key: 'situacao',
      header: 'Situação',
      render: (credencial) => (
        <StatusBadge
          label={credencial.ativa ? 'Ativa' : 'Inativa'}
          tone={credencial.ativa ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'acoes',
      header: '',
      align: 'right',
      width: '170px',
      render: (credencial) =>
        can(ACAO.AlterarSituacaoCredencial) ? (
          <Button
            variant={credencial.ativa ? 'danger' : 'success'}
            size="sm"
            iconLeft={credencial.ativa ? <PowerOff size={14} /> : <ShieldCheck size={14} />}
            onClick={(e) => {
              e.stopPropagation()
              setAlvo(credencial)
            }}
          >
            {credencial.ativa ? 'Desativar' : 'Ativar'}
          </Button>
        ) : null,
    },
  ]

  return (
    <>
      <PageHeader
        title="Credenciais"
        subtitle="Consulte e ajuste as credenciais que o crawler usa nos portais"
      />

      <FilterBar
        searchValue={busca}
        onSearchChange={setBusca}
        onSearchClear={() => efetivar({ ...aplicado, busca: '' })}
        searchPlaceholder="Buscar por credencial, código do prestador ou operadora"
        searchLabel="Buscar credenciais"
        isSearching={isFetching && busca !== buscaDebounced}
        chips={montarChips(aplicado, (campo) =>
          efetivar({ ...aplicado, [campo]: filtroVazio[campo] }),
        )}
        activeCount={contarFiltrosDoPainel(aplicado)}
        onApply={() => efetivar({ ...rascunho, busca: aplicado.busca })}
        onClear={() => efetivar(filtroVazio)}
        quickFilters={
          <ChipSelect
            options={situacaoCredencialBadges}
            value={rascunho.situacao === STATUS_CREDENCIAL.Todas ? [] : [rascunho.situacao]}
            onChange={alternarSituacao}
            ariaLabel="Situação da credencial"
          />
        }
        advanced={
          <>
            <FormField label="Operadora" htmlFor="operadoraId">
              <LookupSelect
                id="operadoraId"
                termo={termoOperadora}
                onTermoChange={setTermoOperadora}
                itens={operadoras.data ?? []}
                isLoading={operadoras.isFetching}
                selecionado={operadoraEscolhida}
                placeholder="Preencha o nome ou o registro ANS"
                onSelecionar={(item) => {
                  setOperadoraEscolhida(item)
                  alterarRascunho('operadoraId', item?.id ?? '')
                }}
              />
            </FormField>

            <FormField label="Cliente" htmlFor="clienteId">
              <LookupSelect
                id="clienteId"
                termo={termoCliente}
                onTermoChange={setTermoCliente}
                itens={clientes.data ?? []}
                isLoading={clientes.isFetching}
                selecionado={clienteEscolhido}
                placeholder="Preencha o nome ou o CNPJ"
                onSelecionar={(item) => {
                  setClienteEscolhido(item)
                  alterarRascunho('clienteId', item?.id ?? '')
                }}
              />
            </FormField>
          </>
        }
      />

      <DataTable
        columns={columns}
        onRowClick={(credencial) => navigate(`/credenciais/${credencial.id}`)}
        page={page}
        getRowKey={(credencial) => credencial.id ?? ''}
        isLoading={isFetching}
        error={error}
        onRetry={() => void refetch()}
        onPageChange={setPageNumber}
        pageSize={pageSize}
        onPageSizeChange={(novoTamanho) => {
          setPageSize(novoTamanho)
          setPageNumber(1)
        }}
        emptyTitle="Nenhuma credencial encontrada"
        emptyDescription="Ajuste os filtros e tente novamente."
      />

      <ConfirmDialog
        isOpen={Boolean(alvo)}
        toggle={() => setAlvo(null)}
        title={alvo?.ativa ? 'Desativar credencial' : 'Ativar credencial'}
        message={
          alvo?.ativa
            ? `A credencial ${orDash(alvo?.nome)} deixará de ser usada nas autorizações deste cliente. As guias que dependem dela param de ser autorizadas pelo crawler.`
            : `A credencial ${orDash(alvo?.nome)} voltará a ser usada nas autorizações deste cliente.`
        }
        confirmLabel={alvo?.ativa ? 'Desativar credencial' : 'Ativar credencial'}
        tone={alvo?.ativa ? 'danger' : 'success'}
        isLoading={atualizarSituacao.isPending}
        onConfirm={() => {
          if (!alvo?.id) return

          atualizarSituacao.mutate(
            { credenciaisId: alvo.id, ativa: !alvo.ativa },
            { onSuccess: () => setAlvo(null) },
          )
        }}
      />
    </>
  )
}

export default CredenciaisList
